import { useDeferredValue, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UsersRound } from 'lucide-react';
import { api } from '../lib_api';

function dateLabel(value?: string | null) {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export default function Users() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const deferredSearch = useDeferredValue(search.trim());
  const users = useQuery({
    queryKey: ['admin', 'users', deferredSearch, page],
    queryFn: async () => (await api.get('/api/admin/users', { params: { search: deferredSearch, page, limit: 100 } })).data,
    refetchInterval: 30000,
  });

  return (
    <section className="panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-darkCard">
        <div>
          <div className="flex items-center gap-2"><UsersRound className="h-4 w-4 text-indigo-500" /><h3 className="text-base font-semibold">Platform users</h3></div>
          <p className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">{users.data?.total ?? 0} registered accounts</p>
        </div>
        <label className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input className="input w-full pl-9" placeholder="Search name or email" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left dark:bg-white/5"><tr><th className="px-3 py-2">User</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">MT5 accounts</th><th className="px-3 py-2">Subscription</th><th className="px-3 py-2">Joined</th></tr></thead>
          <tbody>
            {(users.data?.items || []).map((user: any) => (
              <tr key={user.id} className="border-t border-gray-100 align-top dark:border-darkCard">
                <td className="px-3 py-3"><strong className="block">{user.fullName || 'Unnamed user'}</strong><span className="text-xs text-mutedGray dark:text-darkTextSecondary">{user.email}</span></td>
                <td className="px-3 py-3"><span className="rounded bg-gray-100 px-2 py-1 text-xs dark:bg-white/10">{user.role}</span></td>
                <td className="px-3 py-3"><span className="block">{user.phone || 'Not added'}</span><span className="text-xs text-mutedGray dark:text-darkTextSecondary">{user.phoneVerifiedAt ? 'Verified' : 'Unverified'}</span></td>
                <td className="px-3 py-3 font-semibold">{user.tradingAccountCount}</td>
                <td className="px-3 py-3"><span className={`rounded px-2 py-1 text-xs font-semibold ${user.subscription?.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'}`}>{user.subscription?.active ? user.subscription.planName : 'None'}</span>{user.subscription?.expiresAt ? <span className="mt-1 block text-xs text-mutedGray dark:text-darkTextSecondary">Until {dateLabel(user.subscription.expiresAt)}</span> : null}</td>
                <td className="px-3 py-3 text-xs">{dateLabel(user.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.isLoading ? <p className="p-5 text-sm text-mutedGray">Loading users…</p> : null}
        {users.isError ? <p className="p-5 text-sm text-red-600">Unable to load platform users.</p> : null}
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs dark:border-darkCard">
        <span>Page {users.data?.page || page} of {Math.max(1, Math.ceil(Number(users.data?.total || 0) / 100))}</span>
        <div className="flex gap-2"><button className="rounded border border-gray-200 px-3 py-1.5 disabled:opacity-40 dark:border-darkCard" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button><button className="rounded border border-gray-200 px-3 py-1.5 disabled:opacity-40 dark:border-darkCard" disabled={page >= Math.ceil(Number(users.data?.total || 0) / 100)} onClick={() => setPage((current) => current + 1)}>Next</button></div>
      </div>
    </section>
  );
}
